// AVRASTRO OS 0.6 — connected to avrastro.com:
//   INBOX   what citizens and visitors sent through the website (a share of saved reports, an appointment request,
//           a message). Nothing becomes a CRM record until the advisor accepts it; a citizen's withdrawal is recorded.
//   PORTAL  what the advisor publishes to a client's private page on avrastro.com — only for clients who allowed it, only
//           what the advisor writes or picks (never internal notes), withdrawable at any time.
//   DRAFTS  optional AI drafting (messages, summaries, meeting notes, review agendas) — always reviewed by the advisor.
// These need the account and the internet; everything accepted into the CRM then works offline like any other record.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  acceptIntake, recordIntakeWithdrawal, matchIntakePerson, tasksForIntake, updateTask, addNote, sharedResultsText, sharedPoliciesText, intakeContact,
  type IntakeInput, type IntakePayload, type Person,
} from '@vero/domain';
import { PlatformError, type AiUsage, type IntakeItem, type PortalGrant, type PortalItem } from '@vero/platform';
import { useRuntime } from '../core/runtime.tsx';
import { Empty, Field, Modal, PageHead, PersonCell, PersonPicker, Seg, useConfirm } from '../core/ui.tsx';
import { Icon } from '../core/icons.tsx';
import { navigate, personPath } from '../core/router.ts';
import { accountService } from '../core/account-service.ts';
import { useAccount } from './account.tsx';
import { ClientReportsCard } from './client-reports.tsx';
import { PURCHASE_UI } from '../core/edition.ts';
import { useVideoStatus, videoErr } from './video.tsx';
import { linkVideoRoom } from '@vero/domain';

type Ev = { target: HTMLInputElement };
type TaEv = { target: HTMLTextAreaElement };
type SelEv = { target: HTMLSelectElement };

/** the signed-in platform client, or why it is not available */
function useConnected(feature?: string) {
  const a = useAccount();
  const [online, setOnline] = useState(() => typeof navigator === 'undefined' || navigator.onLine !== false);
  useEffect(() => { const on = () => setOnline(true), off = () => setOnline(false); window.addEventListener('online', on); window.addEventListener('offline', off); return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); }; }, []);
  const reason = !a?.signedIn ? 'signed_out' : !online ? 'offline' : feature && a.licence && !a.licence.features.includes(feature as never) && a.licence.plan !== 'trial' ? 'plan' : null;
  return { a, reason, api: reason ? null : accountService()?.api ?? null };
}
function NotConnected({ reason, icon }: { reason: string; icon: string }) {
  const { t } = useRuntime();
  return (
    <Empty icon={icon}>
      <span data-testid={`web-${reason}`}>{t(`web.reason.${reason}`)}</span>
      {reason !== 'offline' && (reason !== 'plan' || PURCHASE_UI) && <div style={{ marginTop: 10 }}><button className="btn btn-primary" onClick={() => navigate('/account')}>{t(reason === 'plan' ? 'web.see_plans' : 'web.connect')}</button></div>}
    </Empty>
  );
}
function useErrText() {
  const { t } = useRuntime();
  return (e: unknown) => { const code = e instanceof PlatformError ? e.message : String((e as Error)?.message ?? e); const k = `web.err.${code}`; const x = t(k); return x === k ? t('account.err.generic', { code }) : x; };
}
const kindIcon = (k: string) => (k === 'share' ? 'document' : k === 'booking' ? 'agenda' : 'mail');
type Rt = ReturnType<typeof useRuntime>;
const bookingStatus = (i: IntakeItem) => String((i.payload as IntakePayload).status ?? '');
function whenText(rt: Rt, iso?: string) { return iso ? `${rt.i18n.date(iso.slice(0, 10))} ${new Date(iso).toLocaleTimeString(rt.locale, { hour: '2-digit', minute: '2-digit' })}` : ''; }

/** accept an inbox item into the CRM (one changeset), tell the platform, return the person */
async function acceptItem(rt: Rt, item: IntakeItem, o: { personId?: string | null; createNew?: boolean }): Promise<string | null> {
  const { t } = rt;
  const api = accountService()?.api;
  if (!api) return null;
  const p = item.payload as IntakePayload;
  const c = intakeContact(p);
  let pid: string | null = null;
  const input: IntakeInput = { id: item.id, ref: item.ref, kind: item.kind, receivedAt: item.receivedAt, payload: p };
  const cs = await rt.run((v, ctx) => {
    const r = acceptIntake(v, input, {
      personId: o.personId ?? null, createNew: o.createNew,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone, officeAddress: rt.advisor.officeAddress ?? null,
      text: { shareNote: t('web.inbox.note_share'), shareTask: t('web.inbox.task_share'), bookingNote: t('web.inbox.task_booking'), bookingConfirmed: t('web.booking.task_confirmed'), leadNote: t('web.inbox.note_lead'), leadTask: t('web.inbox.task_lead'), callbackTask: t('web.inbox.task_callback'), scope: t('web.inbox.scope'),
        bestTimeMorning: t('web.inbox.best_time.morning'), bestTimeMidday: t('web.inbox.best_time.midday'), bestTimeAfternoon: t('web.inbox.best_time.afternoon'), opinionFile: t('web.inbox.opinion_file') },
    }, ctx);
    pid = r.personId;
    return r.cs;
  }, 'web.inbox.accepted', { name: c.name || c.email });
  if (cs && pid) { await api.intakeAck(item.id, 'accepted', pid); return pid; }
  return null;
}

/**
 * 1.4: a website booking the advisor confirmed (or confirms now): who is this person? The CRM looks for them by e-mail and
 * phone; the advisor says "this is my client X", "another client" or "someone new" — then the appointment is in the agenda
 * and the person's card opens, ready for the meeting.
 */
export function BookingWho({ item, confirm, onDone }: { item: IntakeItem; confirm?: boolean; onDone?: () => void }) {
  const rt = useRuntime();
  const { t, view } = rt;
  const errText = useErrText();
  const p = item.payload as IntakePayload;
  const c = intakeContact(p);
  const match = useMemo(() => matchIntakePerson(view, p), [item.id]);
  const via = match && c.email && (match.primaryEmail ?? '').toLowerCase() === c.email ? 'email' : 'phone';
  const [picking, setPicking] = useState(false);
  const [picked, setPicked] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const video = useVideoStatus();
  const go = async (o: { personId?: string | null; createNew?: boolean }) => {
    const api = accountService()?.api;
    if (!api) return;
    setBusy(true);
    try {
      // requirement 01: a video booking confirmed with the AVRASTRO Video add-on gets its own room; the website's
      // confirmation then carries that guest link instead of the advisor's static video link (a guest link never carries a
      // confidential meeting's end-to-end key: the website stores and e-mails it — the key is sent separately)
      let room: { id: string; guestUrl: string } | null = null;
      if (confirm && p.mode === 'video' && video?.entitled && p.startsAt) {
        try {
          const minutes = p.endsAt ? Math.max(15, Math.round((Date.parse(String(p.endsAt)) - Date.parse(String(p.startsAt))) / 60000)) : 45;
          room = await api.videoCreate({ title: `${t('enum.taskKind.appointment')} · ${c.name || c.email}`, startsAt: String(p.startsAt), durationMin: minutes, lang: rt.locale === 'en' ? 'en' : 'el' });
        } catch (e) { rt.toast(videoErr(rt, e), 'error'); }
      }
      if (confirm) {
        await api.bookingAction(item.id, 'confirm', undefined, { videoUrl: room?.guestUrl ?? null });
        (p as { status?: string }).status = 'confirmed';
      }
      const pid = await acceptItem(rt, item, o);
      if (room && pid) {
        const task = rt.ws.view.rows('tasks').find((x) => x.kind === 'appointment' && (x.data as { intakeRef?: string } | null)?.intakeRef === item.ref);
        if (task) {
          await rt.run((v, ctx) => linkVideoRoom(v, task.id, { roomId: room!.id }, ctx, t('vcall.location')));
          await api.videoUpdate(room.id, { taskId: task.id, personId: pid }).catch(() => undefined);
        }
      }
      rt.close();
      onDone?.();
      if (pid) { rt.toast(t(confirm ? 'web.booking.done_confirmed' : 'web.booking.done'), 'success'); navigate(personPath(pid)); }
    } catch (e) { rt.toast(errText(e), 'error'); } finally { setBusy(false); }
  };
  const topic = p.topicLabel || p.topic;
  return (
    <Modal title={t(confirm ? 'web.booking.confirm_title' : 'web.booking.who_title')} footer={<button className="btn btn-ghost" onClick={() => { rt.close(); onDone?.(); }} data-testid="booking-later">{t('web.booking.later')}</button>}>
      <div className="stack" style={{ gap: 12 }} data-testid="booking-who">
        <div className="card card-pad">
          <b>{c.name || c.email || c.phone}</b>
          <div className="small muted">{[c.email, c.phone].filter(Boolean).join(' · ')}</div>
          <div className="small" style={{ marginTop: 6 }}><b>{whenText(rt, p.startsAt)}</b>{topic ? ` · ${topic}` : ''}{p.mode ? ` · ${t(`web.inbox.mode.${p.mode}`)}` : ''}{p.site ? ` · ${t('web.inbox.via_site', { site: p.site })}` : ''}</div>
          {p.note && <div className="small muted" style={{ marginTop: 4 }}>{p.note}</div>}
        </div>
        <p style={{ margin: 0 }}>{t(confirm ? 'web.booking.confirm_question' : 'web.booking.question', { name: c.name || c.email })}</p>
        {match
          ? <div className="callout small" data-testid="booking-match">{t(`web.booking.found_${via}`, { name: match.displayName })}</div>
          : <div className="callout small muted">{t('web.booking.not_found')}</div>}
        <div className="stack" style={{ gap: 8 }}>
          {match && <button className="btn btn-primary" disabled={busy} onClick={() => void go({ personId: match.id })} data-testid="booking-is-match">{t('web.booking.is_match', { name: match.displayName })}</button>}
          {!picking
            ? <button className="btn" disabled={busy} onClick={() => setPicking(true)} data-testid="booking-existing">{t('web.booking.existing')}</button>
            : <div className="row" style={{ gap: 8 }}><div className="grow"><PersonPicker value={picked} onChange={setPicked} autoFocus /></div><button className="btn btn-primary" disabled={busy || !picked} onClick={() => void go({ personId: picked })}>{t('web.inbox.link')}</button></div>}
          <button className={`btn${match ? '' : ' btn-primary'}`} disabled={busy} onClick={() => void go({ createNew: true })} data-testid="booking-new">{t('web.booking.new_prospect')}</button>
        </div>
        <p className="tiny muted" style={{ margin: 0 }}>{t(confirm ? 'web.booking.confirm_note' : 'web.booking.note')}</p>
      </div>
    </Modal>
  );
}

/** 1.4: while the CRM is open (signed in, online), a booking confirmed on the website asks at once who the person is */
export function BookingWatcher() {
  const rt = useRuntime();
  const { api } = useConnected('website_inbox');
  const asked = useRef(new Set<string>());
  useEffect(() => {
    if (!api) return;
    let stopped = false;
    const check = async () => {
      if (stopped || document.hidden || document.querySelector('.modal, .overlay')) return;
      try {
        const { items } = await api.intake('open');
        const next = items.find((i) => i.kind === 'booking' && i.status === 'open' && bookingStatus(i) === 'confirmed' && !asked.current.has(i.id));
        if (!next || stopped || document.querySelector('.modal, .overlay')) return;
        asked.current.add(next.id);
        rt.open(<BookingWho item={next} />);
      } catch { /* offline, signed out or no plan: the inbox shows it later */ }
    };
    const first = setTimeout(() => void check(), 1500);
    const every = setInterval(() => void check(), 5 * 60_000);
    const onFocus = () => void check();
    window.addEventListener('focus', onFocus);
    return () => { stopped = true; clearTimeout(first); clearInterval(every); window.removeEventListener('focus', onFocus); };
  }, [api]);
  return null;
}

// ------------------------------------------------------------------------------------------------ INBOX
export function InboxView() {
  const rt = useRuntime();
  const { t, i18n, view } = rt;
  const { reason, api } = useConnected('website_inbox');
  const errText = useErrText();
  const [tab, setTab] = useState<'open' | 'all'>('open');
  const [items, setItems] = useState<IntakeItem[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const load = useCallback(async () => {
    if (!api) return;
    setErr(null);
    try { setItems((await api.intake(tab)).items); } catch (e) { setErr(errText(e)); }
  }, [api, tab]);
  useEffect(() => { void load(); }, [load]);
  // a citizen withdrew (or the share expired): record it on the linked person, then archive the item
  useEffect(() => {
    if (!api || !items) return;
    const ended = items.filter((i) => (i.status === 'revoked' || i.status === 'expired'));
    if (!ended.length) return;
    void (async () => {
      for (const i of ended) {
        if (i.personId && view.get('persons', i.personId)) await rt.run((v, c) => recordIntakeWithdrawal(v, i.personId!, i.ref, i.status as 'revoked' | 'expired', c, t(`web.inbox.withdrawal_${i.status}`)));
        await api.intakeAck(i.id, 'archived').catch(() => undefined);
      }
      rt.toast(t('web.inbox.withdrawals', { n: ended.length }), 'info');
      void load();
    })();
  }, [items]);
  if (reason) return <div data-testid="inbox"><PageHead title={t('web.inbox.title')} subtitle={t('web.inbox.subtitle')} /><NotConnected reason={reason} icon="mail" /></div>;
  const shown = (items ?? []).filter((i) => tab === 'all' || i.status === 'open' || (i.status === 'accepted' && bookingChanged(i, view)));
  return (
    <div data-testid="inbox">
      <PageHead title={t('web.inbox.title')} subtitle={t('web.inbox.subtitle')} actions={<button className="btn" onClick={() => void load()} data-testid="inbox-refresh"><Icon name="cloud" />{t('web.refresh')}</button>} />
      <div className="people-toolbar"><Seg values={['open', 'all'] as const} value={tab} onChange={setTab} label={(x) => t(`web.inbox.tab.${x}`)} testId="inbox-tabs" /></div>
      {err && <div className="callout warn small" role="alert">{err}</div>}
      {items == null && !err ? <p className="muted small">{t('web.loading')}</p> : shown.length === 0 ? <Empty icon="mail">{t(tab === 'open' ? 'web.inbox.empty' : 'web.inbox.empty_all')}</Empty> :
        <div className="stack" style={{ gap: 12 }}>{shown.map((i) => <InboxCard key={i.id} item={i} onChanged={load} />)}</div>}
      <p className="tiny muted" style={{ marginTop: 14 }}>{t('web.inbox.privacy')} {i18n.date(rt.today)}</p>
    </div>
  );
}
function bookingChanged(i: IntakeItem, view: ReturnType<typeof useRuntime>['view']) {
  const st = String((i.payload as IntakePayload).status ?? '');
  return i.kind === 'booking' && /cancel|declin/.test(st) && tasksForIntake(view, i.ref).length > 0;
}

function InboxCard({ item, onChanged }: { item: IntakeItem; onChanged: () => void }) {
  const rt = useRuntime();
  const { t, i18n, view } = rt;
  const errText = useErrText();
  const confirm = useConfirm();
  const p = item.payload as IntakePayload;
  const c = intakeContact(p);
  const match = useMemo(() => (item.status === 'open' ? matchIntakePerson(view, p) : null), [item.id, item.status, view]);
  const [busy, setBusy] = useState(false);
  const api = accountService()?.api;
  const when = (iso?: string) => (iso ? `${i18n.date(iso.slice(0, 10))} ${new Date(iso).toLocaleTimeString(rt.locale, { hour: '2-digit', minute: '2-digit' })}` : '');
  const accept = async (personId?: string | null) => {
    if (!api) return;
    setBusy(true);
    try { if (await acceptItem(rt, item, { personId: personId ?? null })) onChanged(); }
    catch (e) { rt.toast(errText(e), 'error'); } finally { setBusy(false); }
  };
  const decline = () => confirm(t('web.inbox.decline_confirm'), async () => { try { await api?.intakeAck(item.id, 'declined'); onChanged(); rt.toast(t('web.inbox.declined'), 'info'); } catch (e) { rt.toast(errText(e), 'error'); } });
  // 1.4: a booking that still waits for the advisor is confirmed or declined from here (the website e-mails the client)
  const requested = item.kind === 'booking' && item.status === 'open' && bookingStatus(item) === 'requested';
  const declineBooking = () => confirm(t('web.booking.decline_confirm'), async () => {
    try { await api?.bookingAction(item.id, 'decline'); await api?.intakeAck(item.id, 'declined'); onChanged(); rt.toast(t('web.booking.declined'), 'info'); }
    catch (e) { rt.toast(errText(e), 'error'); }
  });
  const cancelAppointment = async () => {
    for (const task of tasksForIntake(view, item.ref)) await rt.run((v, ctx) => updateTask(v, task.id, { status: 'cancelled', notes: [task.notes, t('web.inbox.cancelled_on_site')].filter(Boolean).join('\n') }, ctx));
    await rt.run((v, ctx) => addNote(v, { personId: item.personId!, title: t('web.inbox.cancelled_on_site'), body: `${p.topicLabel ?? p.topic ?? ''} ${when(p.startsAt)}`.trim() || '—' }, ctx));
    rt.toast(t('web.inbox.appointment_cancelled'), 'success');
    onChanged();
  };
  const status = item.status === 'open' ? null : item.status;
  return (
    <article className="card card-pad inbox-card" data-testid="inbox-item" data-kind={item.kind}>
      <div className="row wrap" style={{ gap: 10, alignItems: 'flex-start' }}>
        <span className="inbox-kind" aria-hidden="true"><Icon name={kindIcon(item.kind)} /></span>
        <div className="grow">
          <div className="row wrap" style={{ gap: 8, alignItems: 'baseline' }}>
            <b>{c.name || c.email || c.phone || '—'}</b>
            <span className="chip gold">{t(`web.inbox.kind.${item.kind}`)}</span>
            {status && <span className={`chip ${status === 'accepted' ? 'ok' : status === 'declined' || status === 'archived' ? 'low' : 'high'}`}>{t(`web.inbox.status.${status}`)}</span>}
            <span className="tiny muted">{t('web.inbox.received', { when: when(item.receivedAt) })}</span>
          </div>
          <div className="small muted">{[c.email, c.phone].filter(Boolean).join(' · ')}</div>
          {item.kind === 'share' && <>
            <p className="small" style={{ margin: '8px 0 4px' }}>{t('web.inbox.share_line', { n: (p.results ?? []).length, until: p.expiresAt ? i18n.date(p.expiresAt.slice(0, 10)) : '—' })}{p.allowPortal ? ` · ${t('web.inbox.portal_allowed')}` : ''}</p>
            {p.message && <blockquote className="small inbox-quote">{p.message}</blockquote>}
            {p.opinion && <p className="small" style={{ margin: '6px 0' }} data-testid="inbox-opinion">
              <b>{t('web.inbox.opinion_file')}</b>: {p.opinion.fileName ?? '—'}
              {p.opinion.fileUrl && /^https:\/\//.test(p.opinion.fileUrl) ? <> · <a href={p.opinion.fileUrl} target="_blank" rel="noopener noreferrer">{t('web.inbox.opinion_open')}</a></> : <> · <span className="muted">{t('web.inbox.opinion_closed')}</span></>}
              {p.opinion.fileUntil && <span className="tiny muted"> · {t('web.inbox.opinion_until', { date: i18n.date(p.opinion.fileUntil.slice(0, 10)) })}</span>}
            </p>}
            {(p.policies ?? []).length > 0 && <details className="small" open><summary>{t('web.inbox.shared_policies')}</summary><pre className="inbox-pre">{sharedPoliciesText(p.policies, { renews: t('web.inbox.policy_renews'), sum: t('web.inbox.policy_sum'), premium: t('web.inbox.policy_premium') })}</pre></details>}
            {(p.results ?? []).length > 0 && <details className="small"><summary>{t('web.inbox.show_results')}</summary><pre className="inbox-pre">{sharedResultsText(p.results)}</pre></details>}
          </>}
          {item.kind === 'booking' && <p className="small" style={{ margin: '8px 0 0' }}><b>{when(p.startsAt)}</b>{p.topicLabel || p.topic ? ` · ${p.topicLabel || p.topic}` : ''}{p.mode ? ` · ${t(`web.inbox.mode.${p.mode}`)}` : ''}{p.status ? ` · ${t(`web.inbox.booking.${String(p.status).replace(/[^a-z_]/g, '')}`)}` : ''}{p.site ? ` · ${t('web.inbox.via_site', { site: p.site })}` : ''}{p.note ? <><br /><span className="muted">{p.note}</span></> : null}</p>}
          {item.kind === 'booking' && item.status === 'open' && bookingStatus(item) === 'confirmed' && <p className="small" style={{ margin: '6px 0 0' }}>{t('web.booking.confirmed_hint')}</p>}
          {item.kind === 'lead' && <>{(p.topicLabel || p.topic || p.kind === 'callback') && <p className="small" style={{ margin: '8px 0 4px' }}>{p.kind === 'callback' && <span className="chip">{t('web.inbox.callback')}</span>} <b>{p.topicLabel || p.topic}</b>{p.bestTime && ['morning', 'midday', 'afternoon'].includes(p.bestTime) ? ` · ${t(`web.inbox.best_time.${p.bestTime}`)}` : ''}{p.site ? ` · ${t('web.inbox.via_site', { site: p.site })}` : ''}</p>}<blockquote className="small inbox-quote">{p.message}</blockquote></>}
          {item.personId && view.get('persons', item.personId) && <div className="small" style={{ marginTop: 6 }}><PersonCell id={item.personId} /></div>}
        </div>
      </div>
      {requested && <div className="row wrap inbox-actions" style={{ gap: 8, marginTop: 12 }}>
        <button className="btn btn-primary" disabled={busy} onClick={() => rt.open(<BookingWho item={item} confirm onDone={onChanged} />)} data-testid="inbox-booking-confirm">{t('web.booking.confirm')}</button>
        <span className="spacer" />
        <button className="btn btn-ghost" disabled={busy} onClick={declineBooking} data-testid="inbox-booking-decline">{t('web.booking.decline')}</button>
      </div>}
      {item.status === 'open' && !requested && <div className="row wrap inbox-actions" style={{ gap: 8, marginTop: 12 }}>
        {match ? <>
          <span className="small muted">{t('web.inbox.matches', { name: match.displayName })}</span>
          <button className="btn btn-primary" disabled={busy} onClick={() => void accept(match.id)} data-testid="inbox-accept">{t('web.inbox.accept_link', { name: match.displayName })}</button>
          <button className="btn" disabled={busy} onClick={() => rt.open(<PickPerson onPick={(id) => void accept(id)} />)}>{t('web.inbox.other_person')}</button>
        </> : <>
          <button className="btn btn-primary" disabled={busy} onClick={() => void accept(null)} data-testid="inbox-accept">{t(item.kind === 'lead' ? 'web.inbox.accept_lead' : 'web.inbox.accept_new')}</button>
          <button className="btn" disabled={busy} onClick={() => rt.open(<PickPerson onPick={(id) => void accept(id)} />)}>{t('web.inbox.existing_person')}</button>
        </>}
        <span className="spacer" />
        <button className="btn btn-ghost" disabled={busy} onClick={decline} data-testid="inbox-decline">{t('web.inbox.decline')}</button>
      </div>}
      {item.status === 'accepted' && bookingChanged(item, view) && <div className="callout warn small" style={{ marginTop: 10 }}>
        {t('web.inbox.booking_changed')} <button className="btn btn-sm" onClick={() => void cancelAppointment()} data-testid="inbox-cancel-appointment">{t('web.inbox.cancel_appointment')}</button>
      </div>}
      {item.status === 'accepted' && item.personId && view.get('persons', item.personId) && <div className="row" style={{ marginTop: 8 }}><button className="btn btn-sm btn-ghost" onClick={() => navigate(personPath(item.personId!))}>{t('web.inbox.open_person')}</button></div>}
    </article>
  );
}
function PickPerson({ onPick }: { onPick: (id: string) => void }) {
  const rt = useRuntime();
  const [id, setId] = useState<string | null>(null);
  return (
    <Modal title={rt.t('web.inbox.existing_person')} footer={<><button className="btn" onClick={rt.close}>{rt.t('common.cancel')}</button><button className="btn btn-primary" disabled={!id} onClick={() => { rt.close(); onPick(id!); }}>{rt.t('web.inbox.link')}</button></>}>
      <PersonPicker value={id} onChange={setId} autoFocus />
    </Modal>
  );
}

// ----------------------------------------------------------------------------------------------- PORTAL
const PORTAL_KINDS = ['summary', 'review', 'document_request', 'appointment', 'note'] as const;
export function PortalView() {
  const rt = useRuntime();
  const { t, i18n, view } = rt;
  const { reason, api } = useConnected('client_portal');
  const errText = useErrText();
  const [grants, setGrants] = useState<PortalGrant[] | null>(null);
  const [items, setItems] = useState<PortalItem[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const confirm = useConfirm();
  const load = useCallback(async () => {
    if (!api) return;
    try { const [g, it] = await Promise.all([api.portalGrants(), api.portalItems()]); setGrants(g.grants); setItems(it.items); setErr(null); } catch (e) { setErr(errText(e)); }
  }, [api]);
  useEffect(() => { void load(); }, [load]);
  if (reason) return <div data-testid="portal"><PageHead title={t('web.portal.title')} subtitle={t('web.portal.subtitle')} /><NotConnected reason={reason} icon="globe" /></div>;
  const personOf = (citizen: string): Person | undefined => view.rows('persons').find((x) => (x.data as { website?: { citizen?: string } } | null)?.website?.citizen === citizen);
  const withdraw = (id: string) => confirm(t('web.portal.withdraw_confirm'), async () => { try { await api!.portalWithdraw(id); rt.toast(t('web.portal.withdrawn'), 'info'); void load(); } catch (e) { rt.toast(errText(e), 'error'); } });
  return (
    <div data-testid="portal">
      <PageHead title={t('web.portal.title')} subtitle={t('web.portal.subtitle')} actions={<button className="btn" onClick={() => void load()}><Icon name="cloud" />{t('web.refresh')}</button>} />
      {err && <div className="callout warn small" role="alert">{err}</div>}
      {api && <ClientReportsCard api={api} />}
      {grants == null && !err ? <p className="muted small">{t('web.loading')}</p> : !grants?.length ? <Empty icon="globe">{t('web.portal.empty')}</Empty> :
        <div className="stack" style={{ gap: 12 }}>{grants.map((g) => {
          const person = personOf(g.citizen);
          const mine = items.filter((x) => x.citizen === g.citizen);
          return (
            <section key={`${g.citizen}:${g.shareRef}`} className="card card-pad" data-testid="portal-client">
              <div className="row wrap" style={{ gap: 10, alignItems: 'center' }}>
                <div className="grow"><b>{person?.displayName ?? g.citizenName ?? '—'}</b><div className="tiny muted">{t('web.portal.allowed_until', { date: i18n.date(g.expiresAt.slice(0, 10)) })}</div></div>
                {person && <button className="btn btn-ghost btn-sm" onClick={() => navigate(personPath(person.id))}>{t('web.inbox.open_person')}</button>}
                <button className="btn btn-primary btn-sm" onClick={() => rt.open(<PublishForm grant={g} person={person ?? null} onDone={load} />)} data-testid="portal-publish">{t('web.portal.publish')}</button>
              </div>
              {mine.length > 0 && <table className="people-table" style={{ marginTop: 10 }}>
                <tbody>{mine.map((x) => <tr key={x.id} data-testid="portal-item"><td><b>{x.title}</b><div className="tiny muted">{t(`web.portal.kind.${x.kind}`)} · {i18n.date(x.publishedAt.slice(0, 10))}</div></td>
                  <td className="small">{x.withdrawnAt ? <span className="chip low">{t('web.portal.withdrawn')}</span> : x.readAt ? <span className="chip ok">{t('web.portal.read', { date: i18n.date(x.readAt.slice(0, 10)) })}</span> : <span className="chip gold">{t('web.portal.unread')}</span>}</td>
                  <td className="num">{!x.withdrawnAt && <button className="btn btn-sm btn-ghost" onClick={() => withdraw(x.id)} data-testid="portal-withdraw">{t('web.portal.withdraw')}</button>}</td></tr>)}</tbody>
              </table>}
            </section>
          );
        })}</div>}
      <p className="tiny muted" style={{ marginTop: 14 }}>{t('web.portal.privacy')}</p>
    </div>
  );
}

/** what the client will see: the advisor writes it or prefills it from the policies — never internal notes */
function PublishForm({ grant, person, onDone }: { grant: PortalGrant; person: Person | null; onDone: () => void }) {
  const rt = useRuntime();
  const { t, i18n, view } = rt;
  const errText = useErrText();
  const [f, setF] = useState({ kind: 'summary' as typeof PORTAL_KINDS[number], title: '', body: '' });
  const [sections, setSections] = useState<{ label: string; value: string }[]>([]);
  const [busy, setBusy] = useState(false);
  const prefill = () => {
    if (!person) return;
    const pols = view.where('policies', 'personId', person.id).filter((x) => x.status === 'active' || x.status === 'pending' || x.status === 'renewed');
    setSections(pols.slice(0, 30).map((x) => ({ label: [x.productName, x.carrier].filter(Boolean).join(' · '), value: [x.annualPremium ? `${i18n.money(x.annualPremium)} / ${t('web.portal.year')}` : '', x.renewalDate ? t('web.portal.renews', { date: i18n.date(x.renewalDate) }) : ''].filter(Boolean).join(' · ') || '—' })));
    setF((x) => ({ ...x, title: x.title || t('web.portal.prefill_title'), kind: 'summary' }));
  };
  const publish = async () => {
    setBusy(true);
    try {
      await accountService()!.api.portalPublish({ citizen: grant.citizen, kind: f.kind, title: f.title, body: f.body, sections: sections.filter((s) => s.label.trim() || s.value.trim()), personId: person?.id ?? null });
      if (person) await rt.run((v, c) => addNote(v, { personId: person.id, title: t('web.portal.note', { title: f.title }), body: [f.body, ...sections.map((s) => `${s.label}: ${s.value}`)].filter(Boolean).join('\n') || '—' }, c));
      rt.toast(t('web.portal.published'), 'success'); rt.close(); onDone();
    } catch (e) { rt.toast(errText(e), 'error'); } finally { setBusy(false); }
  };
  return (
    <Modal wide title={t('web.portal.publish_to', { name: person?.displayName ?? grant.citizenName ?? '—' })} footer={<><button className="btn" onClick={rt.close}>{t('common.cancel')}</button><button className="btn btn-primary" disabled={busy || !f.title.trim()} onClick={() => void publish()} data-testid="portal-publish-save">{t('web.portal.publish')}</button></>}>
      <div className="form-grid">
        <Field label={t('web.portal.kind_label')}><select className="select" value={f.kind} onChange={(e: SelEv) => setF({ ...f, kind: e.target.value as typeof f.kind })}>{PORTAL_KINDS.map((k) => <option key={k} value={k}>{t(`web.portal.kind.${k}`)}</option>)}</select></Field>
        <Field label={t('web.portal.title_label')}><input className="input" value={f.title} maxLength={200} onChange={(e: Ev) => setF({ ...f, title: e.target.value })} data-testid="portal-title" /></Field>
        <Field label={t('web.portal.body_label')} span2 hint={t('web.portal.body_hint')}><textarea className="textarea" rows={6} value={f.body} maxLength={20000} onChange={(e: TaEv) => setF({ ...f, body: e.target.value })} data-testid="portal-body" /></Field>
        <div className="span2 stack" style={{ gap: 6 }}>
          <div className="row wrap" style={{ gap: 8, alignItems: 'center' }}><span className="label">{t('web.portal.sections')}</span><span className="spacer" />
            {person && <button className="btn btn-sm" type="button" onClick={prefill} data-testid="portal-prefill">{t('web.portal.prefill')}</button>}
            <button className="btn btn-sm btn-ghost" type="button" onClick={() => setSections([...sections, { label: '', value: '' }])}>{t('web.portal.add_line')}</button></div>
          {sections.map((s, i) => <div key={i} className="row" style={{ gap: 6 }}>
            <input className="input" aria-label={t('web.portal.line_label')} value={s.label} onChange={(e: Ev) => setSections(sections.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} />
            <input className="input" aria-label={t('web.portal.line_value')} value={s.value} onChange={(e: Ev) => setSections(sections.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))} />
            <button className="btn btn-ghost btn-sm" type="button" aria-label={t('common.delete')} onClick={() => setSections(sections.filter((_, j) => j !== i))}>×</button>
          </div>)}
        </div>
      </div>
      <p className="tiny muted">{t('web.portal.publish_note')}</p>
    </Modal>
  );
}

// ----------------------------------------------------------------------------------------------- DRAFTS
const DRAFT_KINDS = ['message', 'summary', 'meeting', 'review'] as const;
export function DraftsView() {
  const rt = useRuntime();
  const { t } = rt;
  const { reason, api } = useConnected('ai_drafts');
  const errText = useErrText();
  const [usage, setUsage] = useState<AiUsage | null>(null);
  const [f, setF] = useState({ kind: 'message' as typeof DRAFT_KINDS[number], lang: (rt.locale.startsWith('el') ? 'el' : 'en') as 'el' | 'en', input: '' });
  const [draft, setDraft] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [pid, setPid] = useState<string | null>(null);
  useEffect(() => { if (api) void api.aiUsage().then(setUsage).catch(() => undefined); }, [api]);
  if (reason) return <div data-testid="drafts"><PageHead title={t('web.drafts.title')} subtitle={t('web.drafts.subtitle')} /><NotConnected reason={reason} icon="spark" /></div>;
  const run = async () => {
    setBusy(true);
    try { const r = await api!.aiDraft({ kind: f.kind, lang: f.lang, input: f.input }); setDraft(r.draft); setUsage((u) => (u ? { ...u, used: r.used, allowance: r.allowance } : u)); }
    catch (e) { rt.toast(errText(e), 'error'); } finally { setBusy(false); }
  };
  const save = async () => { if (!pid || !draft.trim()) return; await rt.run((v, c) => addNote(v, { personId: pid, title: t(`web.drafts.kind.${f.kind}`), body: draft }, c), 'common.saved'); };
  return (
    <div data-testid="drafts">
      <PageHead title={t('web.drafts.title')} subtitle={t('web.drafts.subtitle')} />
      {usage && !usage.configured && <div className="callout warn small">{t('web.drafts.not_configured')}</div>}
      <div className="grid grid-2" style={{ alignItems: 'start' }}>
        <section className="card card-pad stack">
          <div className="form-grid">
            <Field label={t('web.drafts.kind_label')}><select className="select" value={f.kind} onChange={(e: SelEv) => setF({ ...f, kind: e.target.value as typeof f.kind })} data-testid="drafts-kind">{DRAFT_KINDS.map((k) => <option key={k} value={k}>{t(`web.drafts.kind.${k}`)}</option>)}</select></Field>
            <Field label={t('settings.language')}><select className="select" value={f.lang} onChange={(e: SelEv) => setF({ ...f, lang: e.target.value as 'el' | 'en' })}><option value="el">Ελληνικά</option><option value="en">English</option></select></Field>
            <Field label={t('web.drafts.input')} span2 hint={t('web.drafts.input_hint')}><textarea className="textarea" rows={9} value={f.input} maxLength={8000} onChange={(e: TaEv) => setF({ ...f, input: e.target.value })} data-testid="drafts-input" /></Field>
          </div>
          <div className="row wrap" style={{ gap: 8, alignItems: 'center' }}>
            <button className="btn btn-primary" disabled={busy || !f.input.trim() || (usage != null && (!usage.configured || usage.used >= usage.allowance))} onClick={() => void run()} data-testid="drafts-run">{t(busy ? 'web.drafts.working' : 'web.drafts.run')}</button>
            {usage && <span className="tiny muted" data-testid="drafts-usage">{t('web.drafts.usage', { used: usage.used, allowance: usage.allowance })}</span>}
          </div>
          <p className="tiny muted" style={{ margin: 0 }}>{t('web.drafts.privacy')}</p>
        </section>
        <section className="card card-pad stack">
          <div className="section-title">{t('web.drafts.result')}</div>
          {draft ? <>
            <div className="callout warn small">{t('web.drafts.review')}</div>
            <textarea className="textarea" rows={12} value={draft} onChange={(e: TaEv) => setDraft(e.target.value)} data-testid="drafts-output" />
            <div className="row wrap" style={{ gap: 8, alignItems: 'center' }}>
              <button className="btn" onClick={() => void navigator.clipboard?.writeText(draft).then(() => rt.toast(t('web.drafts.copied'), 'success'))}>{t('web.drafts.copy')}</button>
              <div style={{ minWidth: 220 }}><PersonPicker value={pid} onChange={setPid} /></div>
              <button className="btn" disabled={!pid} onClick={() => void save()}>{t('web.drafts.save_note')}</button>
            </div>
          </> : <p className="muted small">{t('web.drafts.empty')}</p>}
        </section>
      </div>
    </div>
  );
}
